import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/generic/modal";
import { toast } from "react-toastify";
import { ERROR_TOAST_CONFIG } from "../../../helpers/functions";
import { listEventPhotoKeys } from "../../../helpers/myPhotos";
import authManager from "../../../services/authManager";
import EventsService from "../../../services/eventsService";
import { createHashPool, createCompressionPool } from "../../../helpers/photoWorkers";
import { contentHashFromKey, captureTimestamp, uploadMetadata } from "../../../helpers/publicUploads";
import { notifyPhotosChanged } from "../../../helpers/photosChanged";
import { readExifTags } from "../../../helpers/exifDate";
import { runUploadQueue, SlowConnection, UploadMeter } from "../../../helpers/uploadQueue";
import { isNetworkError, putPhoto } from "../../../helpers/photoPut";
import { classifyRepeated, confirmPublished, keysWithStatus, RETRY_LATER, withoutPublicPrefix } from "../../../helpers/publicPhotoChanges";
import { RevAlert, RevButton, RevCard, RevFigure, RevProgress, RevSkeleton, RevSwitch } from "../../../components/ds";

const MAX_DIMENSION = 1920;
const PAGINATION_LIMIT = 1000;
// Una foto que pasa este tiempo sin enviar un byte mientras las demás avanzan se da por colgada:
// se cancela y se reintenta al final. La cola (helpers/uploadQueue) no abre más peticiones que
// conexiones da el navegador al bucket, así que ninguna espera turno sin progreso.
const UPLOAD_STALL_TIME = 60 * 1000;
// A partir de aquí se avisa de que hay fotos sin avanzar. Si no avanza ninguna, la red no responde:
// la cola retiene las que estaban en curso y las relanza, sin arrancar fotos nuevas.
const STALL_NOTICE_TIME = 15 * 1000;
// Espera entre relanzamientos con la red sin responder. Cada relanzamiento se da 15 s para
// avanzar, así que la subida se reanuda como mucho unos 20 s después de que vuelva la red.
const NETWORK_HOLD_DELAYS = [1000, 2000, 5000];
// El aviso de reintento se quita cuando la subida lleva este tiempo avanzando sin parones,
// aunque las reintentadas aún esperen turno al final de la cola.
const RETRY_NOTICE_CLEAR_TIME = 5 * 1000;
// Velocidad de subida recomendada en el aviso de conexión lenta, que sale por debajo de 20 Mbps
// de media y se quita al llegar a la recomendada.
const SLOW_CONNECTION_ADVICE_MBPS = 25;

const MAX_RETRIES = 3;
// Espera antes de reintentar tras un corte de red (el S3 de Amplify 5 no los reintenta: su
// decisor busca name «Network error» y el XHR rechaza con message «Network Error»).
const NETWORK_RETRY_DELAYS = [3000, 10000, 30000];
// Códigos de S3/STS cuando las credenciales temporales ya no valen; se reintenta con unas nuevas.
const EXPIRED_CREDENTIALS_ERRORS = ["ExpiredToken", "ExpiredTokenException", "RequestExpired"];

const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

// Milisegundos hasta reintentar, o false si el error no se reintenta. Sin conexión espera al
// evento `online` (Infinity): reintentar sin red solo gastaría intentos.
function uploadRetryDelay(error, attempt) {
  if (error?.stalled) return isOffline() ? Infinity : 0;
  if (error?.name === "NotAuthorizedException" ||
    EXPIRED_CREDENTIALS_ERRORS.includes(error?.name) || error?.message?.includes("token expired")) return 0;
  if (isNetworkError(error)) {
    if (isOffline()) return Infinity;
    return NETWORK_RETRY_DELAYS[Math.min(attempt, NETWORK_RETRY_DELAYS.length) - 1];
  }
  return false;
}

// Espera antes de relanzar las fotos retenidas porque la red no responde.
const networkHoldDelay = (round) =>
  isOffline() ? Infinity : NETWORK_HOLD_DELAYS[Math.min(round, NETWORK_HOLD_DELAYS.length - 1)];

// Al volver la conexión, los reintentos en espera salen ya.
const subscribeOnline = (wake) => {
  window.addEventListener("online", wake);
  return () => window.removeEventListener("online", wake);
};
const VALID_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

// Rótulo del botón principal (en reposo, en curso) según lo que haya en la selección.
const UPLOAD_ACTION_LABELS = {
  upload: ["eventPhotos.upload.uploadNow", "eventPhotos.upload.uploading"],
  update: ["eventPhotos.upload.updateNow", "eventPhotos.upload.updating"],
  uploadAndUpdate: ["eventPhotos.upload.uploadAndUpdateNow", "eventPhotos.upload.uploadingAndUpdating"],
};

// Resultado de la subida de fotos nuevas. Con fallos, las que faltan siguen seleccionadas.
const SUMMARY_NAMES = 5;
const UploadSummary = ({ summary, t, onRetry, retryDisabled, onClose }) => {
  const { total, uploaded, failed } = summary;
  const names = failed.slice(0, SUMMARY_NAMES).map(item => item.file.name).join(", ") +
    (failed.length > SUMMARY_NAMES ? " " + t("eventPhotos.uploadSummary.more", { count: failed.length - SUMMARY_NAMES }) : "");
  return (
    <RevAlert tone={failed.length ? "err" : "ok"} role={failed.length ? "alert" : "status"} className="rev-public-changes"
      actions={<>
        {onRetry && <RevButton variant="secondary" size="sm" disabled={retryDisabled} onClick={onRetry}>
          {t("eventPhotos.uploadSummary.retry", { count: failed.length })}
        </RevButton>}
        <RevButton variant="subtle" icon size="sm" aria-label={t("generic.alertButton.close")} onClick={onClose}>
          <span className="material-icons rev-icon-sm" aria-hidden="true">close</span>
        </RevButton>
      </>}>
      <span>
        {failed.length
          ? `${t("eventPhotos.uploadSummary.partial", { uploaded, count: total })} ${t("eventPhotos.uploadSummary.failed", { count: failed.length, names })}`
          : t("eventPhotos.uploadSummary.done", { count: uploaded })}
      </span>
    </RevAlert>
  );
};

// Resumen de las repetidas que pasan a la galería pública.
const PublicChangeSummary = ({ summary, t, onRetry, retrying }) => {
  const lines = [];
  if (summary.busy) lines.push(t("publicGallery.changes.busy", { count: summary.busy }));
  if (summary.publishing) {
    lines.push(summary.progress != null
      ? t("publicGallery.publish.progress", { count: summary.publishing, added: summary.progress })
      : t("publicGallery.publish.inProgress", { count: summary.publishing }));
  } else {
    if (summary.added) lines.push(t("publicGallery.publish.done", { count: summary.added }));
    if (summary.pending) lines.push(t(summary.full ? "publicGallery.publish.pendingFull" : "publicGallery.publish.pending", { count: summary.pending }));
  }
  if (summary.notAdded) lines.push(t("publicGallery.publish.failed", { count: summary.notAdded }));
  if (summary.needsSupport) lines.push(t("publicGallery.changes.needsSupport", { count: summary.needsSupport }));
  if (lines.length === 0) return null;
  const problems = summary.notAdded || summary.needsSupport || summary.busy;
  return (
    <RevAlert tone={problems ? "warn" : summary.publishing ? "info" : "ok"} role="status" className="rev-public-changes"
      actions={onRetry && <RevButton variant="secondary" size="sm" loading={retrying} disabled={retrying} onClick={onRetry}>
        {t("publicGallery.publish.retry")}
      </RevButton>}>
      <span>{lines.join(" · ")}</span>
    </RevAlert>
  );
};

/**
 * Tarjeta de subida de fotos al evento, con su motor (hash, compresión, multipart, reintentos).
 * - `toPublic` (pestaña «Galería pública», solo el dueño): cada foto se sube como foto normal del
 *   evento y además con copia en la galería pública; las repetidas que ya están en el evento se
 *   publican. Siempre abierta, también con los selfies activos, y sin opción de comprimir.
 * - Sin `toPublic` (Subir fotos, dueño y colaborador): solo fotos normales; `locked` la cierra
 *   (selfies activos) y en su lugar se pinta `lockedContent`.
 * `onStatus` informa a la página de { loading, busy, total } para lo que pinta fuera de la tarjeta.
 */
const PhotoUploader = ({ eventId: id, user, toPublic = false, locked = false, lockedContent = null, pending = false, title, sub, onStatus }) => {
  const { t } = useTranslation();
  const history = useHistory();

  // ======== Estados principales ========
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [photoListError, setPhotoListError] = useState(false);
  const photoControlsLocked = loading || uploading || photoListError;

  const [counter, setCounter] = useState(0);

  const [handlerCounter, setHandlerCounter] = useState(-1);
  const [handlerTotal, setHandlerTotal] = useState(-1);

  const [allCurrentPhotos, setAllCurrentPhotos] = useState([]);
  const [duplicatedPhotos, setDuplicatedPhotos] = useState([]);

  const [readyToUpload, setReadyToUpload] = useState([]);
  // Repetidas que ya están en el evento (suyas o de colaboradores), por su clave existente.
  const [repeatedExisting, setRepeatedExisting] = useState([]);
  // Claves que se ven en la galería pública; null si no se ha podido consultar.
  const [publicKeys, setPublicKeys] = useState(null);
  // Publicaciones aceptadas que el pipeline aún no ha confirmado: cuentan ya como públicas.
  const publishingKeys = useRef(new Set());
  const [changeSummary, setChangeSummary] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  // Cada lote de cambios lleva su número: la confirmación de uno anterior no pisa el resumen del siguiente.
  const changeBatch = useRef(0);
  // El componente puede seguir montado al cambiar de evento: lo de un evento no se aplica en otro.
  const activeEventId = useRef(id);
  // Varias fotos del evento pueden tener el mismo contenido (dueño y colaborador): todas cambian.
  const existingByHash = useRef(new Map());
  const knownHashes = useRef(new Set());
  const resetSelectionAfterRefresh = useRef(false);
  const analyzing = useRef(false);
  const refreshCapacity = async () => {
    const response = await EventsService.publicUploads(id);
    return response.data;
  };
  const refreshPublicKeys = async () => {
    const response = await EventsService.publicPhotos(id);
    const listed = new Set((response.data?.photos || []).map(p => withoutPublicPrefix(p.photoKey)));
    if (activeEventId.current === id) setPublicKeys(listed);
    return listed;
  };
  const [compresionEnabled, setCompresionEnabled] = useState(false);
  const [showCompressionModal, setShowCompressionModal] = useState(false);
  // ======== Pools de workers ========
  const hashPoolRef = useRef(null);
  const compressPoolRef = useRef(null);

  // Progreso por bytes, velocidad, fotos sin avance y fotos esperando reintento.
  const [uploadStatus, setUploadStatus] = useState(null);
  // Resultado de la última subida: se queda a la vista hasta cerrarlo o elegir más fotos.
  const [uploadSummary, setUploadSummary] = useState(null);
  const [offline, setOffline] = useState(() => navigator.onLine === false);
  useEffect(() => {
    const update = () => setOffline(navigator.onLine === false);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  if (!hashPoolRef.current) hashPoolRef.current = createHashPool();
  if (!compressPoolRef.current) compressPoolRef.current = createCompressionPool();

  useEffect(() => {
    return () => {
      hashPoolRef.current.terminateAll();
      compressPoolRef.current.terminateAll();
    };
  }, []);

  // ========================

  useEffect(() => {
    activeEventId.current = id;
    publishingKeys.current = new Set();
    changeBatch.current += 1;
    setPublicKeys(null);
    setChangeSummary(null);
    setRepeatedExisting([]);
  }, [id]);

  useEffect(() => {
    if (id && user && !uploading) {
      getCurrentPhotos().catch((error) => {
        console.error(error);
        setLoading(false);
      });
    }
    // eslint-disable-next-line
  }, [id, user]);

  useEffect(() => {
    onStatus?.({ loading, busy: photoControlsLocked, total: allCurrentPhotos?.length || 0 });
    // eslint-disable-next-line
  }, [loading, photoControlsLocked, allCurrentPhotos]);

  // Bloquear navegación si estamos subiendo
  useEffect(() => {
    if (uploading) {
      const unblock = history.block(t("generic.alertButton.leavePageOnUploading"));
      return () => {
        unblock();
      };
    }
    // eslint-disable-next-line
  }, [uploading]);

  const getCurrentPhotos = async () => {
    setLoading(true);
    try {
      const { all } = await listEventPhotoKeys(id, user.id);
      setAllCurrentPhotos(all);
      const byHash = new Map();
      for (const key of all) {
        const hash = contentHashFromKey(key);
        byHash.set(hash, [...(byHash.get(hash) || []), key]);
      }
      existingByHash.current = byHash;
      const selectedHashes = resetSelectionAfterRefresh.current ? [] : readyToUpload.map(p => contentHashFromKey(p.key));
      knownHashes.current = new Set([...all.map(contentHashFromKey), ...selectedHashes]);
      if (resetSelectionAfterRefresh.current) {
        setReadyToUpload([]);
        setDuplicatedPhotos([]);
        setRepeatedExisting([]);
        setCounter(0);
        resetSelectionAfterRefresh.current = false;
      }
      setPhotoListError(false);
      if (toPublic) refreshPublicKeys().catch(() => setPublicKeys(null));
    } catch (e) {
      console.error(e);
      setPhotoListError(true);
    } finally {
      setLoading(false);
    }
  };

  const cleanHooks = async () => {
    // Keep the current hashes and selection until S3 has returned every page.
    // On failure the retry must finish this same refresh before accepting new files.
    resetSelectionAfterRefresh.current = true;
    await getCurrentPhotos();
  };

  // Read the selected original once, before optional compression. Missing EXIF is explicit.
  // Own EXIF reader (first 64 KB only, no exif-js): hotfix 1b18433 ported from production.
  const getFileDate = async (file) => {
    try { return captureTimestamp(await readExifTags(file)); } catch { return null; }
  };

  // Workers
  async function calculateFileHash(file) {
    return hashPoolRef.current.runTask(file);
  }

  async function compressImage(file) {
    return compressPoolRef.current.runTask({ file, maxDimension: MAX_DIMENSION });
  }

  // Un intento de subida. La cola vigila el avance y aborta `signal` si la foto se cuelga o la red
  // no responde; putPhoto lo traduce a Storage.cancel, que es lo único que corta un Storage.put.
  async function uploadOneFile(file, key, metadata, signal, onProgress) {
    try {
      return await putPhoto(key, file, {
        contentType: file.type || "image/jpeg", // fallback
        metadata,
        // Storage.put (Amplify 5) no renueva credenciales: firma con la copia que guardó el último
        // Storage.list/get, que caduca a la hora con la página abierta (ExpiredToken). Se le pasan
        // las del Identity Pool recién comprobadas.
        credentials: authManager.ensureFreshStorageCredentials(),
        onProgress,
        signal,
      });
    } catch (error) {
      if (!signal.aborted) {
        const masked = error?.maskedBy ? ` (Amplify lo tapaba con ${error.maskedBy.name})` : "";
        console.error(`[UPLOAD] ${file.name} - ${error?.name}: ${error?.message}${masked}`);
      }
      throw error;
    }
  }

  // Evitar que cierre la pestaña si está subiendo
  function handleBeforeUnload(e) {
    e.preventDefault();
    e.returnValue = "";
  }

  // En la galería pública, las repetidas que ya están en el evento y aún no son públicas se publican.
  const toPublish = toPublic && publicKeys
    ? classifyRepeated(repeatedExisting, new Set([...publicKeys, ...publishingKeys.current]), true).toPublish
    : [];
  const hasWork = readyToUpload.length > 0 || toPublish.length > 0;
  // El rótulo dice qué va a pasar: subir las nuevas, publicar las repetidas o las dos cosas.
  const uploadAction = readyToUpload.length > 0
    ? (toPublish.length > 0 ? "uploadAndUpdate" : "upload")
    : (toPublish.length > 0 ? "update" : "upload");

  const onUpload = () => {
    if (locked || photoControlsLocked || analyzing.current || !hasWork) return;
    runUpload();
  };

  // Resultado de publicar (se confirma releyendo la galería). Lo que otra operación retenía o no
  // llegó a empezar se puede reintentar; lo atascado va a soporte.
  const applyPublicChanges = async (publishKeys) => {
    const eventId = id;
    const batch = ++changeBatch.current;
    const summary = { batch, busy: 0, needsSupport: 0, publishing: 0, added: 0, pending: 0, full: false, notAdded: 0, retry: [] };
    const { results } = await EventsService.publishPublic(eventId, publishKeys);
    const accepted = keysWithStatus(results, "accepted");
    summary.added = keysWithStatus(results, "alreadyPublic").length;
    summary.needsSupport = keysWithStatus(results, "needsSupport").length;
    summary.busy = keysWithStatus(results, ...RETRY_LATER).length;
    summary.retry = keysWithStatus(results, "failed", ...RETRY_LATER);
    summary.notAdded = keysWithStatus(results, "failed", "notFound").length;
    summary.publishing = accepted.length;
    accepted.forEach(key => publishingKeys.current.add(key));
    if (accepted.length > 0) confirmInBackground(eventId, batch, accepted, summary);
    if (mounted.current && activeEventId.current === eventId && changeBatch.current === batch) setChangeSummary({ ...summary });
  };

  const confirmInBackground = (eventId, batch, accepted, summary) => {
    const current = () => mounted.current && activeEventId.current === eventId;
    // Solo actualiza el resumen de su propio lote.
    const update = (change) => setChangeSummary(previous => previous?.batch === batch ? { ...previous, ...change(previous) } : previous);
    confirmPublished(() => EventsService.publicPhotos(eventId), accepted, {
      isCancelled: () => !current(),
      onProgress: ({ added }) => current() && update(() => ({ progress: added })),
    }).then(result => {
      accepted.forEach(key => publishingKeys.current.delete(key));
      if (!current()) return;
      setPublicKeys(result.listed);
      update(() => ({ publishing: 0, progress: undefined, added: summary.added + result.added, pending: result.pending, full: result.full }));
      notifyPhotosChanged(eventId);
    });
  };

  // El cupo es de la galería entera: las fotos nuevas y las repetidas que se publican ocupan plaza.
  // Comprobación previa de todo el lote, antes de mandar un solo byte.
  const capacityAllows = async (selected) => {
    try {
      const capacity = await refreshCapacity();
      if (selected <= capacity.available) return true;
      toast.error(t("publicGallery.batchFull", { available: capacity.available, selected }));
    } catch {
      toast.error(t("publicGallery.capacityError"));
    }
    return false;
  };

  const retryKeys = changeSummary?.retry || [];
  const retryChanges = async () => {
    if (retryKeys.length === 0 || uploading) return;
    setUploading(true);
    try {
      if (!(await capacityAllows(retryKeys.length))) return;
      setChangeSummary(null);
      await applyPublicChanges(retryKeys);
    } catch (error) {
      console.error("[PUBLIC] retry", error);
      toast.error(t("publicGallery.changes.error"), ERROR_TOAST_CONFIG);
    } finally {
      setUploading(false);
      notifyPhotosChanged(id);
    }
  };

  const runUpload = async () => {
    const publishKeys = toPublish;
    if (readyToUpload.length === 0 && publishKeys.length === 0) return;
    setUploading(true);
    setChangeSummary(null);
    setUploadSummary(null);
    if (toPublic && !(await capacityAllows(readyToUpload.length + publishKeys.length))) {
      setUploading(false);
      return;
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    authManager.startUploadKeepAlive();
    // Las fotos nuevas y las repetidas que se publican van por separado: un fallo no para lo otro.
    const changes = publishKeys.length > 0
      ? applyPublicChanges(publishKeys).catch(error => {
        console.error("[PUBLIC] changes", error);
        toast.error(t("publicGallery.changes.error"), ERROR_TOAST_CONFIG);
      })
      : Promise.resolve();
    let result;
    try {
      [, result] = await Promise.all([changes, uploadSelection()]);
    } finally {
      authManager.stopUploadKeepAlive();

      await cleanHooks();
      // Las que no han subido vuelven a la selección (si el listado se ha refrescado), para reintentarlas.
      if (result && mounted.current && activeEventId.current === id) {
        if (result.failed.length > 0 && !resetSelectionAfterRefresh.current) {
          result.failed.forEach(item => knownHashes.current.add(contentHashFromKey(item.key)));
          setReadyToUpload(result.failed);
        }
        setUploadSummary(result);
      }
      // La copia pública la crea el pipeline unos segundos después: al verla, se avisa al contador.
      if (toPublic && result) {
        const failedKeys = new Set(result.failed.map(item => item.key));
        const uploadedKeys = result.keys.filter(key => !failedKeys.has(key));
        const eventId = id;
        if (uploadedKeys.length > 0) confirmPublished(() => EventsService.publicPhotos(eventId), uploadedKeys, {
          isCancelled: () => !mounted.current || activeEventId.current !== eventId,
        }).then(({ listed }) => {
          if (!mounted.current || activeEventId.current !== eventId) return;
          setPublicKeys(listed);
          notifyPhotosChanged(eventId);
        });
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
      setUploading(false);
      setCounter(0);
      notifyPhotosChanged(id);
    }
  };

  const uploadSelection = async () => {
    if (readyToUpload.length === 0) return;
    const items = readyToUpload.map(item => {
      item.metadata ??= uploadMetadata(item.file, item.capturedAt, toPublic);
      return item;
    });

    // Cada foto pesa su tamaño original; el avance es el de su fichero real (comprimido o no).
    const meter = new UploadMeter(items.map(item => ({ id: item.key, size: item.file.size })));
    const connection = new SlowConnection({ slowBelowMbps: 20, recoveredFromMbps: SLOW_CONNECTION_ADVICE_MBPS });
    // Fotos que esperan reintento; cada una sale en cuanto su nuevo intento envía bytes. El aviso
    // sigue mientras la subida no avance y se quita cuando lleva un rato avanzando sin parones,
    // aunque las reintentadas aún esperen turno al final de la cola.
    const retrying = new Map();
    let lastRetryAt = 0;
    let networkStalled = false;
    let uploaded = 0;
    const failed = [];
    const refreshStatus = () => {
      if (!mounted.current) return;
      const attempts = [...retrying.values()];
      const speedMbps = meter.speedMbps();
      const recovered = meter.steadyFor() >= RETRY_NOTICE_CLEAR_TIME && Date.now() - lastRetryAt >= RETRY_NOTICE_CLEAR_TIME;
      const showRetrying = attempts.length > 0 && !recovered;
      setUploadStatus({
        percent: meter.percent(),
        speedMbps,
        // Ya se había enviado algo y ahora no sale nada.
        paused: meter.sent > 0 && speedMbps === 0,
        stuck: meter.stuck(STALL_NOTICE_TIME),
        networkStalled,
        slowConnection: connection.update(meter.sent),
        retrying: showRetrying ? attempts.length : 0,
        attempt: showRetrying ? Math.max(...attempts) : 0,
      });
    };
    setUploadStatus({ percent: 0, speedMbps: 0, paused: false, stuck: 0, networkStalled: false, slowConnection: false, retrying: 0, attempt: 0 });
    const statusTimer = setInterval(refreshStatus, 1000);

    // Comprimir si compresionEnabled; la cola lo adelanta y reparte sitio con el tamaño ya comprimido.
    const prepare = async (item) => {
      if (!compresionEnabled) return item;
      try {
        const compressed = await compressImage(item.file);
        return { ...item, file: compressed };
      } catch (err) {
        console.error("[COMPRESSION]", item.file.name, err);
        toast.error(t("eventPhotos.upload.compressError", { name: item.file.name }), ERROR_TOAST_CONFIG);
        return item;
      }
    };

    try {
      await runUploadQueue(items, {
        prepare,
        upload: (ready, attempt, { signal, onProgress }) => {
          meter.start(ready.key);
          return uploadOneFile(ready.file, ready.key, ready.metadata, signal, (loaded, total) => {
            meter.progress(ready.key, loaded, total);
            onProgress(loaded);
            if (loaded > 0) retrying.delete(ready.key);
          });
        },
        maxRetries: MAX_RETRIES,
        retryDelay: uploadRetryDelay,
        stallMs: UPLOAD_STALL_TIME,
        quietMs: STALL_NOTICE_TIME,
        holdDelay: networkHoldDelay,
        subscribeWake: subscribeOnline,
        onHold: (ready) => {
          meter.finish(ready.key, false);
          refreshStatus();
        },
        onNetworkStall: (stalled) => {
          if (stalled) console.error(`[UPLOAD] ninguna foto avanza en ${STALL_NOTICE_TIME / 1000} s: se retienen y se relanzan`);
          networkStalled = stalled;
          refreshStatus();
        },
        onUploaded: (ready) => {
          meter.finish(ready.key, true);
          retrying.delete(ready.key);
          uploaded++;
          setCounter((c) => c + 1);
          refreshStatus();
        },
        onRetry: (ready, attempt, error) => {
          if (error?.stalled) console.error(`[UPLOAD] ${ready.file.name} - sin avance en ${UPLOAD_STALL_TIME / 1000} s, cancelada`);
          meter.finish(ready.key, false);
          retrying.set(ready.key, attempt);
          lastRetryAt = Date.now();
          refreshStatus();
        },
        onFailed: (ready, error) => {
          meter.finish(ready.key, false);
          retrying.delete(ready.key);
          console.error(`[BATCH] Error uploading:`, error);
          // Se avisa en el resumen final; vuelve a la selección con su fichero original.
          failed.push(items.find(item => item.key === ready.key) || ready);
          refreshStatus();
        },
      });
    } finally {
      clearInterval(statusTimer);
      if (mounted.current) setUploadStatus(null);
    }
    return { total: items.length, uploaded, failed, keys: items.map(item => item.key) };
  };

  // ================================

  // Procesar ficheros por slices (igual que antes)
  const processFileSlice = async (files) => {
    const newReady = [];
    const newDuplicated = [];
    const newRepeated = [];

    for (const file of files) {
      try {
        // Calculamos hash
        const key = await getKeyHash(file);
        const hash = contentHashFromKey(key);
        if (knownHashes.current.has(hash)) {
          newDuplicated.push(key);
          newRepeated.push(...(existingByHash.current.get(hash) || []));
        } else {
          // One set survives all slices and selections; add immediately, before the next file.
          knownHashes.current.add(hash);
          const capturedAt = await getFileDate(file);
          newReady.push({ file, key, capturedAt });
        }
        setHandlerCounter((prev) => prev + 1);
      } catch (e) {
        console.error("Error en processFileSlice:", e);
        toast.error(t("eventPhotos.upload.readError", { name: file.name }));
      }
    }

    setReadyToUpload((prev) => [...prev, ...newReady]);
    setDuplicatedPhotos((prev) => [...prev, ...newDuplicated]);
    setRepeatedExisting((prev) => [...new Set([...prev, ...newRepeated])]);
  };

  // Procesar todos en bloques
  const handleFilesPagination = async (allFiles) => {
    let startIndex = 0;
    const totalFiles = allFiles.length;
    try {
      while (startIndex < totalFiles) {
        const endIndex = startIndex + PAGINATION_LIMIT;
        const fileSlice = allFiles.slice(startIndex, endIndex);
        await processFileSlice(fileSlice);
        startIndex = endIndex;
      }
    } catch (e) {
      console.error(e);
      toast.error(t("eventPhotos.upload.analyzeError"));
    } finally {
      setHandlerCounter(-1);
    }
  };

  // Validar tipos de archivo
  const validateFileTypes = (files) => {
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      if (VALID_IMAGE_TYPES.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      toast.warning(
        t("eventPhotos.upload.invalidFileTypes")
      );
    }

    return validFiles;
  };

  // Selección (click o drop)
  const handleSelectFiles = async (files) => {
    if (analyzing.current || photoControlsLocked || locked) return;
    const validFiles = validateFileTypes(files);
    if (validFiles.length === 0) return;
    setUploadSummary(null);

    setHandlerTotal(validFiles.length);
    setHandlerCounter(0);
    analyzing.current = true;
    try { await handleFilesPagination(validFiles); }
    finally { analyzing.current = false; }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const chosenFiles = Array.from(e.dataTransfer.files);
    handleSelectFiles(chosenFiles);
  };

  // Generar key a partir de hash
  const getKeyHash = async (file) => {
    const hash = await calculateFileHash(file);
    const selectedExt = (file.name.split(".").pop() || "jpg").toLowerCase();
    const ext = selectedExt === "jpeg" ? "jpg" : selectedExt;
    return `${id}/FtpUsers/${user.id}/${hash}.${ext}`;
  };

  const ToastCompressionTextInfo = () => {
    return (
      <p className="rev-help">{t("eventPhotos.upload.compressModalBody")}</p>
    );
  };

  // Por bytes mientras sube (las fotos pesan lo que pesan); al terminar, por fotos subidas.
  const uploadPercent = uploadStatus
    ? uploadStatus.percent
    : readyToUpload?.length ? Math.round(((counter || 0) / readyToUpload.length) * 100) : 0;

  const inputId = toPublic ? "uploadPublicPhotosInput" : "uploadPhotosInput";

  // ======== Render ========
  return (
    <RevCard title={title} sub={sub}>
      {photoListError && (
        <RevAlert tone="err" role="alert" actions={
          <RevButton variant="secondary" loading={loading} disabled={uploading}
            onClick={() => getCurrentPhotos()}>
            {t("eventPhotos.upload.retryRefresh")}
          </RevButton>
        }>
          <span>{t("eventPhotos.upload.refreshError")}</span>
        </RevAlert>
      )}
      <div className="rev-upload-grid">
        <div className="rev-upload-drop-col">
          {uploading && readyToUpload.length === 0 ? (
            <div className="rev-upload-progress">
              <RevAlert tone="info">
                <span>{t("publicGallery.changes.applying")}</span>
              </RevAlert>
            </div>
          ) : uploading ? (
            <div className="rev-upload-progress">
              {uploadStatus?.slowConnection && <RevAlert tone="warn" role="status">
                <span>{t("eventPhotos.uploading.slowConnection", { mbps: SLOW_CONNECTION_ADVICE_MBPS })}</span>
              </RevAlert>}
              <RevAlert tone="info">
                <span>{t("eventPhotos.uploading.keepVisible")}</span>
              </RevAlert>
              <div className="rev-upload-progress__row">
                <span>
                  <span className="rev-num">{counter || 0}</span>
                  {" / "}
                  <span className="rev-num">{readyToUpload?.length || 0}</span>
                </span>
                <span className="rev-num">{uploadPercent} %</span>
              </div>
              <RevProgress value={uploadPercent} max={100} />
              <span className="rev-help">
                {uploadStatus?.paused
                  ? <>— Mbps ({t("eventPhotos.uploading.paused")})</>
                  : <><span className="rev-num">{(uploadStatus?.speedMbps || 0).toFixed(2)}</span> Mbps</>}
              </span>
              {offline ? (
                <RevAlert tone="warn" role="status">
                  <span>{t("eventPhotos.uploading.offline")}</span>
                </RevAlert>
              ) : uploadStatus?.networkStalled ? (
                <RevAlert tone="warn" role="status">
                  <span>{t("eventPhotos.uploading.noResponse")}</span>
                </RevAlert>
              ) : uploadStatus?.retrying > 0 ? (
                <RevAlert tone="warn" role="status">
                  <span>{t("eventPhotos.uploading.retrying", { count: uploadStatus.retrying, attempt: uploadStatus.attempt, max: MAX_RETRIES })}</span>
                </RevAlert>
              ) : uploadStatus?.stuck > 0 ? (
                <RevAlert tone="warn" role="status">
                  <span>{t("eventPhotos.uploading.stuck", { count: uploadStatus.stuck })}</span>
                </RevAlert>
              ) : null}
            </div>
          ) : locked ? (
            lockedContent
          ) : pending ? (
            <RevSkeleton kind="title" count={3} />
          ) : handlerCounter < 0 ? (
            <>
              <label
                htmlFor={inputId}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                aria-disabled={photoControlsLocked}
                className="rev-drop rev-drop--fill"
              >
                <span className="rev-icon-muted">
                  <span className="material-icons">backup</span>
                </span>
                <span className="rev-drop__title">{t("eventPhotos.upload.dropTitle")}</span>
                <span className="rev-help">{t("eventPhotos.upload.dropHint")}</span>
              </label>
              <input
                id={inputId}
                hidden
                draggable
                type="file"
                multiple
                disabled={photoControlsLocked}
                accept="image/png, image/jpeg"
                onChange={(e) => {
                  const chosenFiles = Array.from(e.target.files);
                  handleSelectFiles(chosenFiles);
                }}
              />
            </>
          ) : (
            <div className="rev-upload-progress">
              <RevProgress value={handlerCounter} max={handlerTotal} />
              <span className="rev-help">
                <span className="rev-num">{handlerCounter}</span>
                {" / "}
                <span className="rev-num">{handlerTotal}</span>
                {" "}
                {t("eventPhotos.upload.analyzing")}
              </span>
            </div>
          )}
        </div>

        {!locked && !pending && <div className="rev-upload-stats-col">
          <div className="rev-upload-stat">
            <span className="rev-upload-stat__label">{t("eventPhotos.upload.ready")}</span>
            <RevFigure size="lg">
              {loading ? <RevSkeleton kind="cifra" /> : (readyToUpload?.length > 0 ? readyToUpload.length : "—")}
            </RevFigure>
            <span className="rev-upload-stat__note">{t(toPublic ? "publicGallery.upload.readyNote" : "eventPhotos.upload.readyNote")}</span>
          </div>
          <div className="rev-upload-stat">
            <span className="rev-upload-stat__label">{t("eventPhotos.upload.duplicated")}</span>
            <RevFigure size="lg">
              {loading ? <RevSkeleton kind="cifra" /> : (duplicatedPhotos?.length > 0 ? duplicatedPhotos.length : "—")}
            </RevFigure>
            <span className="rev-upload-stat__note">{t(toPublic ? "publicGallery.upload.duplicatedNote" : "eventPhotos.upload.duplicatedNote")}</span>
          </div>
          {/* La galería pública sube siempre el original, sin comprimir. */}
          {!toPublic && <div className="rev-upload-stat">
            <div className="rev-upload-stat__switchrow">
              <RevSwitch
                as="button"
                bare
                role="switch"
                aria-checked={compresionEnabled}
                aria-label={t("eventPhotos.upload.compress")}
                on={compresionEnabled}
                disabled={photoControlsLocked}
                onClick={() => setCompresionEnabled(!compresionEnabled)}
              />
              <span className="rev-upload-stat__label">{t("eventPhotos.upload.compress")}</span>
              <RevButton variant="subtle" icon size="sm" aria-label={t("eventPhotos.upload.compressInfoAria")} onClick={() => setShowCompressionModal(true)}>
                <span className="material-icons rev-icon-sm" aria-hidden="true">info_outline</span>
              </RevButton>
            </div>
            <span className="rev-upload-stat__note">
              {t(compresionEnabled ? "eventPhotos.upload.compressOnNote" : "eventPhotos.upload.compressOffNote", { maxDimension: MAX_DIMENSION })}
            </span>
          </div>}
        </div>}
      </div>

      {!uploading && !locked && toPublish.length > 0 && (
        <RevAlert tone="info" className="rev-public-changes">
          <span>{t("publicGallery.publish.willAdd", { count: toPublish.length })}</span>
        </RevAlert>
      )}
      {!uploading && toPublic && publicKeys === null && repeatedExisting.length > 0 && (
        <RevAlert tone="warn" className="rev-public-changes">
          <span>{t("publicGallery.changes.unknown")}</span>
        </RevAlert>
      )}
      {!uploading && uploadSummary && <UploadSummary summary={uploadSummary} t={t}
        onRetry={uploadSummary.failed.length > 0 && readyToUpload.length > 0 && !locked ? onUpload : null}
        retryDisabled={photoControlsLocked || handlerCounter >= 0}
        onClose={() => setUploadSummary(null)} />}
      {changeSummary && <PublicChangeSummary summary={changeSummary} t={t}
        retrying={uploading} onRetry={retryKeys.length > 0 ? retryChanges : null} />}
      {!uploading && readyToUpload.length > 0 && <details className="rev-public-selection">
        <summary>{t("publicGallery.selection", { count: readyToUpload.length })}</summary>
        <ul>{readyToUpload.map(item => <li key={item.key}>
          <span>{item.file.name}</span>
          <RevButton icon size="sm" aria-label={t("publicGallery.removeSelection", { name: item.file.name })}
            disabled={photoControlsLocked || handlerCounter >= 0}
            onClick={() => {
              knownHashes.current.delete(contentHashFromKey(item.key));
              setReadyToUpload(current => current.filter(p => p.key !== item.key));
            }}><span className="material-icons rev-icon-sm">close</span></RevButton>
        </li>)}</ul>
      </details>}
      {!locked && !pending && <div className="rev-upload-footer">
        <div className="rev-upload-footer__actions">
          <RevButton
            variant="secondary"
            disabled={photoControlsLocked || handlerCounter >= 0 || (readyToUpload.length < 1 && duplicatedPhotos.length < 1)}
            onClick={() => {
              knownHashes.current = new Set(allCurrentPhotos.map(contentHashFromKey));
              setUploadSummary(null);
              setReadyToUpload([]);
              setDuplicatedPhotos([]);
              setRepeatedExisting([]);
            }}
          >
            {t("eventPhotos.upload.clearSelection")}
          </RevButton>
          <RevButton variant="primary" onClick={onUpload} disabled={photoControlsLocked || handlerCounter >= 0 || !hasWork}>
            {t(UPLOAD_ACTION_LABELS[uploadAction][uploading ? 1 : 0])}
          </RevButton>
        </div>
      </div>}

      {showCompressionModal && (
        <Modal
          title={t("eventPhotos.upload.compressModalTitle", { maxDimension: MAX_DIMENSION })}
          showConfirmButton={true}
          showCancelButton={false}
          confirmButtonText={t("eventPhotos.upload.compressModalConfirm")}
          customComponent={<ToastCompressionTextInfo />}
          onConfirm={() => setShowCompressionModal(false)}
          onCancel={() => setShowCompressionModal(false)}
        />
      )}
    </RevCard>
  );
};

export default PhotoUploader;
